import { getAPIHeaders } from '../utils/apiHeaders';

// Environment configuration for AI API
// Use empty string for Vite proxy in dev, full URL in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
export const AI_API_URL = `${API_BASE_URL}/api/ai`;

// AI API service
export class AIService {
  constructor() {
    // No need to store API key in frontend anymore
  }

  async generateComparison(sourceLanguage: string, targetLanguage: string, concept: string, codeSnippet?: string, provider?: any) {
    try {
      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify({
          type: 'comparison',
          sourceLanguage,
          targetLanguage,
          concept,
          codeSnippet,
          provider
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API Error: ${response.status}`);
      }

      // Get HTML content directly (not JSON)
      const htmlContent = await response.text();
      
      return {
        title: `${sourceLanguage} to ${targetLanguage}: ${concept}`,
        description: `Comparing ${concept} between ${sourceLanguage} and ${targetLanguage}`,
        html: htmlContent
      };
    } catch (error) {
      console.error('Error calling AI API:', error);
      throw error;
    }
  }

  async generateExplanation(
    element: string,
    sourceLanguage: string,
    targetLanguage: string,
    options?: {
      context?: string;      // Full code context (±N lines)
      lineNumber?: number;   // Line number where token appears
      provider?: any;        // Provider preference (fast/quality model)
    }
  ) {
    try {
      const requestBody: any = {
        type: 'explanation',
        element,
        sourceLanguage,
        targetLanguage
      };

      // Add context if provided (for dynamic insights)
      if (options?.context) {
        requestBody.context = options.context;
        requestBody.lineNumber = options.lineNumber;
      }

      // Add provider if provided (for fast/quality model selection)
      if (options?.provider) {
        requestBody.provider = options.provider;
      }

      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Backend API Error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling AI API:', error);
      throw error;
    }
  }

  async generateLensHTML(
    code: string,
    knownLanguage: string,
    targetLanguage: string,
    concept?: string,
    provider?: any,
    options?: { enableLanguagePairPrompts?: boolean; enablePromptCaching?: boolean }
  ) {
    try {
      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify({
          type: 'lens-html',
          code,
          sourceLanguage: knownLanguage,
          targetLanguage,
          concept,
          provider,
          // Pass admin settings to backend (if provided)
          enableLanguagePairPrompts: options?.enableLanguagePairPrompts,
          enablePromptCaching: options?.enablePromptCaching
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API Error: ${response.status}`);
      }

      // Get HTML content directly
      const htmlContent = await response.text();

      return {
        title: `${knownLanguage} to ${targetLanguage}${concept ? `: ${concept}` : ''}`,
        description: `Interactive code with lens explanations`,
        html: htmlContent
      };
    } catch (error) {
      console.error('Error calling AI API for lens HTML:', error);
      throw error;
    }
  }

  /**
   * Generate code with inline comment insights
   * @param code - Code to analyze
   * @param knownLanguage - Source language (e.g., 'C')
   * @param targetLanguage - Target language (e.g., 'Rust')
   * @param provider - Optional provider override
   * @returns Code with inline INSIGHT comment blocks
   */
  async generateLensComments(
    code: string,
    knownLanguage: string,
    targetLanguage: string,
    provider?: any
  ): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/lens-comments`, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify({
          code,
          sourceLanguage: knownLanguage,
          targetLanguage,
          provider
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API Error: ${response.status}`);
      }

      // Get plain text (Rust code with comments)
      const codeWithComments = await response.text();
      return codeWithComments;
    } catch (error) {
      console.error('Error calling AI API for lens comments:', error);
      throw error;
    }
  }

  async regenerateInsight(
    originalInsight: { surface: string; concept: string; paradigm: string; code: string },
    fullCode: string,
    insightIndex: number,
    sourceLanguage: string,
    targetLanguage: string,
    provider?: any
  ) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/regenerate-insight`, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify({
          originalInsight,
          fullCode,
          insightIndex,
          sourceLanguage,
          targetLanguage,
          provider
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API Error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling AI API for insight regeneration:', error);
      throw error;
    }
  }
}
